import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styles from './CoverUnit.module.scss';
import { ClockCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@src/utils/time';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { 
  IconSkill,
  IconExercises,
  IconComforts,
  IconMoods,
  IconStructures,
  IconStandalone,
  categorySkillsOptions,
  categoryStructureOptions,
  categoryComfortLevelOptions,
  categoryMoodOptions1,
  categoryExerciseStyleOptions,
 } from '@src/constants';
import { Unit } from '@src/api/graphql-codegen';
import { getMediaAbsolutePath } from '@src/services/File';
import { useQueryDictionary } from '@src/hooks';
import ImageGrade from '@src/assets/img/grade_hat.png';
import { useMediaQuery } from 'react-responsive';
import useDragScrolling from '../customize-units/card/useDragScrolling';
import { useDrag } from 'react-dnd';
import IconRemovUnit from 'src/assets/icons/remove_unit.svg?react';
import Tooltip from '../tooltip/Tooltip';
import { LazyLoadImage } from 'react-lazy-load-image-component';

type Props = {
  unit: Unit;
  type?: string;
  source?: string;
  target?: string;
  handleEditUnit?: (unit: Unit) => void;
  handleOpenUnit?: (unit: Unit) => void;
  handleLeftArrowAction?: (unit: Unit) => void;
  handleRightArrowAction?: (unit: Unit) => void;
  handleCardHovered?: (unitId: string, hovered: boolean) => void;
  handleSelectedCenterCard?: (centerCard: boolean) => void
  cardHovered?: boolean;
  hoveredUnitId?: string | "";
  index?: number;
  isSelected?: boolean;
  programID?: string | "";
  handleMovableNewCover?: (inNew: boolean) => void;
}

function CoverUnit(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const { unit, type="type", source, programID, handleOpenUnit, handleRightArrowAction, handleMovableNewCover } = props;
  const programOptions = useQueryDictionary({ dictionary: 'Programs' });
  const isMobile = useMediaQuery({ maxWidth: 915 });
  const { id, name, subHeading, categories, isNewCover, suggestedLessonCount, suggestedDuration, progress, coverImage, isComingSoon, comingSoonAt, isNew, isUpdated} = unit;

  const unitCategories = useMemo(() => JSON.parse(categories || '[]'), [categories]);
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();

  const [{ opacity, isDragging }, drag] = useDrag(
    () => ({
      type,
      item: () => {
        // like begin drag
        addEventListenerForWindow();
        return { unit }
      },
      end: () => {
        // like end drag
        removeEventListenerForWindow();
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
        opacity: !!monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [unit, type],
  )

  const [structure, setStructure] = useState('')
  const [skill, setSkill] = useState('')
  const [comfortLevel, setComfortLevel] = useState('')
  const [mood, setMood] = useState('')
  const [exerciseStyle, setExerciseStyle] = useState('')

  useEffect(() => {
    if (isDragging) {
      handleMovableNewCover?.(isNewCover ? isNewCover : false)
    }
  }, [isDragging, isNewCover, handleMovableNewCover])

  useEffect(() => {
    if (unitCategories['Structure']) {
      setStructure(unitCategories['Structure'])
    }
    if (unitCategories['Skill']) {
      setSkill(unitCategories['Skill'])
    }
    if (unitCategories['Comfort Level']) {
      setComfortLevel(unitCategories['Comfort Level'])
    }
    if (unitCategories['Mood']) {
      setMood(unitCategories['Mood'])
    }
    if (unitCategories['Exercise Style']) {
      setExerciseStyle(unitCategories['Exercise Style'])
    }
  }, [unitCategories])

  const comingSoonDate = useMemo(() => {
    if (isComingSoon && comingSoonAt) {
      let d;
      if (comingSoonAt.includes('-')) {
        d = new Date('2023-09-23');
      } else {
        d = new Date(parseInt(comingSoonAt));
      }
      var dateString = formatDate(d.valueOf(), 'MMM dd, yyyy')
      return dateString;
    } else {
      return ''
    }
  }, [isComingSoon, comingSoonAt])

  const onClickUnit = useCallback(() => {
    handleOpenUnit?.(unit)
  }, [handleOpenUnit, unit])

  const actionContent = useMemo(() => {
    switch (source) {
      case 'grade': {
        return <div className={styles.iconWrapper}>
          <Tooltip position={'right'} tooltip={'Remove Content'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(unit)} className={styles.arrowLeftIcon}/>
          </Tooltip>    
        </div>
      }
      case 'addition': {
        return <div className={styles.iconWrapper}>
          <Tooltip position={'right'} tooltip={'Remove Content'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(unit)} className={styles.arrowLeftIcon}/>
          </Tooltip>
        </div>
      }
      default: {
        return <>
        </>
      }
    }
  }, [unit, source, handleRightArrowAction]);

  const label = useMemo(() => {
    if (isNew) {
      return (
        <div className={styles.labelContainer}>NEW</div>
      )
    } else if (isUpdated) {
      return (
        <div className={styles.labelContainer}>UPDATED</div>
      )
    } else if (isComingSoon) {
      return (
        <div className={styles.labelContainer}>UPDATE COMING {comingSoonDate}</div>
      )
    }
  }, [isNew, isUpdated, isComingSoon, comingSoonDate])

  const gradeLabel = useMemo(() => {
    return (programOptions.find(p => p.value === programID)?.label ?? "").replace(' Program', '')
  }, [programID, programOptions])

  const gradeBar = (
    <div className={styles.gradeBar}>
      <img src={ImageGrade} alt='grade' />
      <span>{gradeLabel}</span>
    </div>
  )

  const progressBar = (
    <div className={styles.progressBackBar} style={{borderBottomLeftRadius: isNewCover ? '0' : '10px', borderBottomRightRadius: isNewCover ? '0' : '10px'}}>
      <div className={styles.progressBar} style={{width: `${progress}%`, backgroundColor: progress === 100 ? "#62C590" : "#62C590", borderBottomLeftRadius: isNewCover ? '0' : '10px', borderBottomRightRadius: isNewCover ? '0' : progress === 100 ? '10px' : '0' }}>
      </div>
    </div>
  )

  const suggestBar = (
    <div className={styles.suggestView}>
      <div className={styles.lessonsView}>
        <LordIcon src={IconStandalone} isHover={true} colors={{ primary: "#FFF", secondary: "#FFF" }} size={20}/>
        <div className={styles.lessonsText}>{suggestedLessonCount && suggestedLessonCount > 1 ? `${suggestedLessonCount} Lessons` : `${suggestedLessonCount} Lesson`}</div>
      </div>
      {suggestedDuration ? <div className={styles.durationView}>
        <ClockCircleOutlined className={styles.clockIcon}/>
        <div className={styles.durationText}>{`${suggestedLessonCount}`}&nbsp;<span>x</span>&nbsp;{`${suggestedDuration}`}&nbsp;<span>min</span></div>
      </div> : null}
    </div>
  )

  const bottomView = (
    <div className={styles.bottomView}>
      <div className={styles.unitName}>{name}</div>
      <div className={styles.subHeading}>{subHeading}</div>
      {skill ? <div className={[styles.tagBox, styles['skill']].join(' ')}>
        <LordIcon src={IconSkill[categorySkillsOptions.findIndex(o => o.value === skill)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{skill}</span>
      </div> : null}
      <div className={styles.divider} />
      <div className={styles.categoryView}>
      {mood ? <div className={[styles.tagBox, styles['mood']].join(' ')}>
        <LordIcon src={IconMoods[categoryMoodOptions1.findIndex(o => o.value === mood)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
          <span>{mood}</span></div> : null}
      {comfortLevel ? <div className={[styles.tagBox, styles['comfortLevel']].join(' ')}>
        <LordIcon src={IconComforts[categoryComfortLevelOptions.findIndex(o => o.value === comfortLevel)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{comfortLevel}</span></div> : null}
      {structure ? <div className={[styles.tagBox, styles['structure']].join(' ')}>
        <LordIcon src={IconStructures[categoryStructureOptions.findIndex(o => o.value === structure)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{structure}</span></div> : null}
      {exerciseStyle ? <div className={[styles.tagBox, styles['exerciseStyle']].join(' ')}>
        <LordIcon src={IconExercises[categoryExerciseStyleOptions.findIndex(o => o.value === exerciseStyle)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
          <span>{exerciseStyle}</span></div> : null}
      </div>
    </div>
  )

  return (
    <div ref={drag} id={`${programID}_${id}`} className={[isHovered ? styles.newCoverHoveredCard : styles.newCoverCard, source === 'grade' ? styles.innerShadow : ''].join(' ')} 
      style={{height: !isNewCover ? 'min-content' : '', opacity, cursor: isDragging ? 'grabbing' : 'grab'}} 
      onClick={onClickUnit} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className={styles.topView}>
        <LazyLoadImage src={`${coverImage ? getMediaAbsolutePath(coverImage) : ''}`} className={isNewCover ? styles.roundImage : styles.image} alt="cover"></LazyLoadImage>
        {label}
        {(progress && progress > 0) ? progressBar : null}
        {isNewCover ? suggestBar : null}
        {programID && gradeLabel && isNewCover ? gradeBar : null}
        {actionContent}
      </div>
      {isNewCover ? bottomView : null}
    </div>
  );
}

export default CoverUnit

